import { useEffect, useRef, useState } from 'react';
import { Input } from "@/components/ui/input.jsx";

// List of popular tech job titles in the US
export const US_TECH_JOB_TITLES = [
    // Software Development
    "Software Engineer", "Senior Software Engineer", "Software Developer", "Full Stack Developer", "Full Stack Engineer",
    "Front End Developer", "Front End Engineer", "Backend Developer", "Backend Engineer", "Mobile Developer",
    "iOS Developer", "Android Developer", "Web Developer", "Java Developer", "Python Developer",
    "JavaScript Developer", "React Developer", "Angular Developer", "Vue.js Developer", "Node.js Developer",
    "PHP Developer", "Ruby on Rails Developer", "C# Developer", ".NET Developer", "C++ Developer",
    "Golang Developer", "Rust Developer", "TypeScript Developer", "Unity Developer", "Embedded Software Engineer",
    "Game Developer", "WordPress Developer", "Salesforce Developer", "Firmware Engineer", "Software Architect",
    "Technical Lead", "Lead Developer", "Principal Engineer", "Staff Engineer", "Distinguished Engineer",

    // DevOps and Infrastructure
    "DevOps Engineer", "Site Reliability Engineer", "SRE", "Cloud Engineer", "Infrastructure Engineer",
    "System Administrator", "Systems Engineer", "Network Engineer", "Network Administrator", "Cloud Architect",
    "AWS Engineer", "Azure Engineer", "Google Cloud Engineer", "Platform Engineer", "Release Engineer",
    "Build Engineer", "Kubernetes Engineer", "Docker Engineer", "Infrastructure Architect", "DevSecOps Engineer",

    // Data Science and Analytics
    "Data Scientist", "Data Analyst", "Data Engineer", "Business Intelligence Analyst", "BI Developer",
    "Machine Learning Engineer", "ML Engineer", "AI Engineer", "Artificial Intelligence Engineer", "Deep Learning Engineer",
    "Business Analyst", "Data Architect", "Big Data Engineer", "Analytics Engineer", "Statistician",
    "Research Scientist", "Quantitative Analyst", "NLP Engineer", "Computer Vision Engineer", "MLOps Engineer",

    // Product and Project Management
    "Product Manager", "Technical Product Manager", "Senior Product Manager", "Associate Product Manager",
    "Product Owner", "Project Manager", "Program Manager", "Technical Program Manager", "Scrum Master",
    "Agile Coach", "Delivery Manager", "Digital Project Manager", "IT Project Manager", "Product Marketing Manager",

    // Design and UX
    "UX Designer", "UI Designer", "UI/UX Designer", "Product Designer", "Web Designer",
    "Graphic Designer", "Visual Designer", "Interaction Designer", "UX Researcher", "UX Writer",
    "Content Designer", "Information Architect", "Design Manager", "Creative Director", "Design Technologist",

    // QA and Testing
    "QA Engineer", "Quality Assurance Engineer", "QA Analyst", "Test Engineer", "Automation Engineer",
    "Manual Tester", "SDET", "Software Development Engineer in Test", "QA Lead", "Test Manager",
    "Performance Engineer", "Security Engineer", "Penetration Tester", "Ethical Hacker",

    // Management
    "Engineering Manager", "Development Manager", "Software Development Manager", "CTO", "Director of Engineering",
    "VP of Engineering", "Chief Information Officer", "CIO", "Chief Technology Officer", "IT Manager",
    "IT Director", "Technical Director", "R&D Manager", "Technology Manager",

    // Mobile
    "Mobile App Developer", "Mobile Engineer", "React Native Developer", "Flutter Developer", "Swift Developer",
    "Kotlin Developer", "iOS Engineer", "Android Engineer", "Mobile Architect", "Hybrid App Developer",

    // Cybersecurity
    "Security Engineer", "Information Security Analyst", "Cybersecurity Analyst", "Security Architect", "Security Consultant",
    "Vulnerability Analyst", "Security Operations Engineer", "SOC Analyst", "CISO", "Chief Information Security Officer",
    "Identity and Access Management Specialist", "Security Researcher",

    // Specialized Roles
    "Blockchain Developer", "AR/VR Developer", "VR Engineer", "Augmented Reality Engineer", "Quantum Computing Researcher",
    "IoT Developer", "Robotics Engineer", "Robotic Process Automation Developer", "RPA Developer", "Voice Engineer",
    "Computational Linguist", "Growth Engineer", "Growth Hacker", "Technical Evangelist", "Developer Advocate",

    // Support and Operations
    "Technical Support Engineer", "IT Support Specialist", "Systems Specialist", "Database Administrator", "DBA",
    "Network Operations Engineer", "NOC Engineer", "Help Desk Technician", "IT Technician", "Solutions Engineer",
    "Customer Success Engineer", "Implementation Specialist", "Operations Engineer",

    // Entry Level / Early Career
    "Junior Software Engineer", "Entry Level Developer", "Software Engineering Intern", "IT Intern", "Graduate Software Engineer",
    "Junior Front End Developer", "Junior Back End Developer", "Associate Developer", "Associate Engineer", "Code Specialist",
    "Junior QA Engineer", "Junior Data Analyst", "Associate Data Scientist",

    // Executive and Management
    "Chief Digital Officer", "Chief Data Officer", "Chief Innovation Officer", "Chief Product Officer", "VP of Product",
    "Director of Product Management", "Head of Engineering", "Head of Development", "Head of Technology",

    // Content and Marketing Tech
    "Content Manager", "SEO Specialist", "Digital Marketing Specialist", "Growth Marketing Manager", "Marketing Technologist",
    "Marketing Engineer", "Web Analytics Specialist", "CRM Developer", "Email Marketing Specialist",

    // Emerging Fields
    "AI Ethics Researcher", "Sustainability Technical Lead", "Climate Tech Developer", "Digital Health Engineer", "FinTech Developer",
    "EdTech Developer", "PropTech Engineer", "Smart City Solutions Engineer", "Bioinformatics Engineer", "Computational Biologist"
];

export default function JobTitleAutocomplete({
    onSelect,
    placeholder = "Enter job title",
    className = "",
    value = "",
    onChange,
    error = false,
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [lastSelectedValue, setLastSelectedValue] = useState("");
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // Handle clicks outside the component
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Update suggestions when value changes
    useEffect(() => {
        // Don't show suggestions if the value hasn't changed since last selection
        if (value === lastSelectedValue && value !== "") {
            return;
        }

        if (value && value.length > 1) {
            const matches = US_TECH_JOB_TITLES.filter((title) =>
                title.toLowerCase().includes(value.toLowerCase())
            ).slice(0, 7);
            
            setSuggestions(matches);
            // Only show suggestions if we have matches AND the input is focused
            if (matches.length > 0 && document.activeElement === inputRef.current) {
                setShowSuggestions(true);
            } else {
                setShowSuggestions(false);
            }
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
        setHighlightIndex(-1);
    }, [value, lastSelectedValue]);

    const handleSelect = (val) => {
        if (onSelect) onSelect(val);
        if (onChange) onChange({ target: { value: val } });
        setShowSuggestions(false);
        setLastSelectedValue(val);
        
        // Explicitly blur the input to move focus away
        if (inputRef.current) {
            inputRef.current.blur();
        }
    };

    const handleInputChange = (e) => {
        if (onChange) onChange(e);
        // If user is typing, they're not using the last selection anymore
        if (e.target.value !== lastSelectedValue) {
            setLastSelectedValue("");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightIndex((prev) =>
                prev < suggestions.length - 1 ? prev + 1 : prev
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === "Enter" && highlightIndex >= 0 && showSuggestions) {
            e.preventDefault();
            handleSelect(suggestions[highlightIndex]);
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
        }
    };

    return (
        <div className={`relative ${className}`} ref={wrapperRef}>
            <Input
                ref={inputRef}
                placeholder={placeholder}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    // Only show suggestions if we have matches AND the value isn't the last selected one
                    if (value && value.length > 1 && suggestions.length > 0 && value !== lastSelectedValue) {
                        setShowSuggestions(true);
                    }
                }}
                className={`${error 
                    ? 'bg-red-50 border-red-200 hover:border-red-400 focus:border-red-500 focus:ring-red-100' 
                    : 'bg-[#e9f2ff] border-blue-200 hover:border-blue-400 focus:border-blue-500 focus:ring-blue-100'
                } text-gray-800 placeholder-gray-500 rounded-lg h-12 text-base shadow-sm transition-all`}
            />

            {showSuggestions && suggestions.length > 0 && (
                <ul
                    className="absolute z-10 w-full bg-white border mt-1 rounded shadow-lg max-h-60 overflow-y-auto"
                    onMouseDown={(e) => e.preventDefault()} // Prevent blur while clicking on suggestions
                >
                    {suggestions.map((title, index) => (
                        <li
                            key={title}
                            onMouseDown={(e) => {
                                e.preventDefault(); // Prevent default to keep focus
                                handleSelect(title);
                            }}
                            className={`p-2 cursor-pointer ${
                                index === highlightIndex ? "bg-blue-100" : "hover:bg-gray-100"
                            }`}
                        >
                            {title}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}